import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, PlusIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../../lib/hooks/use-translate'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AssignJourneyToCenterInput } from '../../../../lib/types/models/center/center-input'
import { StaffRequestInput } from '../../../../lib/types/models/staff-request/staff-request-input'
import { IdInput } from '../../../../lib/types/utils'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons, {
  ValidationButtonsBaseFormProps
} from '../../../elements/validation-buttons'
import JourneyCardHeader from './journey-card-header'
import TotalNumberOfChildren from './total-number-of-children'

const AssignStaffRequest = ({
  buttonsConfig
}: ValidationButtonsBaseFormProps<AssignJourneyToCenterInput>) => {
  const form = useFormContext<AssignJourneyToCenterInput>()
  const { t } = useTranslate()

  const data = form.watch('staffRequests')
  const centerToJourneys = form.watch('centerToJourneys')

  const columns = useMemo<Column<IdInput<StaffRequestInput>>[]>(
    () => [
      {
        accessorKey: 'qualifications',
        header: 'Qualifications',
        columnHeaderClassName: 'w-1/2',
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`staffRequests.${row.original.id}.qualification`}
            render={({ field }) => (
              <FormItem className="py-2">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Qualification"
                    required
                    options={Object.values(StaffQualificationEnum)
                      .map((qualification) => ({
                        value: qualification,
                        label: t(staffQualificationEnumMapping[qualification].label)
                      }))
                      .filter(
                        (qualification) =>
                          qualification.value !==
                          form
                            .getValues('staffRequests')
                            .find(
                              (staffRequest: StaffRequestInput) =>
                                staffRequest.qualification === qualification.value
                            )?.qualification
                      )}
                    searchable
                    selectedOption={
                      field.value
                        ? {
                            value: field.value,
                            label: t(staffQualificationEnumMapping[field.value].label)
                          }
                        : undefined
                    }
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'numberOfStaff',
        header: 'Nombre',
        columnHeaderClassName: 'w-1/2',
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`staffRequests.${row.original.id}.numberOfStaff`}
            render={({ field }) => (
              <FormItem className="py-2">
                <FormControl>
                  <ControlledInput
                    required
                    {...field}
                    type={'number'}
                    label={'Nombre'}
                    onChange={(val) => field.onChange(Number(val))}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'actions',
        header: '',
        cell: ({ row }) => (
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              const d = data ? [...data] : []
              const newData = d.filter((_, index) => index !== row.original.id)
              form.setValue('staffRequests', newData)
            }}
          />
        )
      }
    ],
    [data]
  )

  return (
    <div className="flex flex-col gap-4">
      <TotalNumberOfChildren />
      <div className="flex flex-col gap-4">
        {centerToJourneys.map((centerToJourney) => (
          <JourneyCardHeader
            key={centerToJourney.journey.id}
            centerToJourney={centerToJourney}
            mode="view"
          />
        ))}
      </div>
      <div className={'flex flex-col gap-3'}>
        <Typography variant={'body-medium'}>Demande d'encadrants</Typography>
        <DataTable<IdInput<StaffRequestInput>>
          data={data?.map((option, index) => ({ ...option, id: index })) ?? []}
          rowCount={data?.length ?? 0}
          overflow
          paginated={false}
          columns={columns}
          disableHover
          displayEmptyState={false}
          footer={
            <Button
              variant={'secondary'}
              icon={PlusIcon}
              label={"Ajouter une demande d'encadrants"}
              type={'button'}
              className={'border-none'}
              onClick={() => {
                const d = data ? [...data] : []
                d.push({ numberOfStaff: 0, isSpecialRequest: false })
                form.setValue('staffRequests', d)
              }}
            />
          }
        />

        {form.formState.errors.staffRequests?.root && (
          <Typography variant={'small'} color={tailwindColors.red.DEFAULT}>
            {form.formState.errors.staffRequests?.root.message}
          </Typography>
        )}
      </div>
      <ValidationButtons buttonsConfig={buttonsConfig} />
    </div>
  )
}

export default AssignStaffRequest
